/**
 * Banner — inline alert (info/success/warning/error). ADR-0007.
 *
 * Replaces 4 ad-hoc copies in Login (local fn), Recover, Reset (×2).
 *
 * Tokens-only, role="alert" for warning/error, role="status" for success/info,
 * Lucide icon left, optional title + body + action slot.
 */
import { type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Icon } from '@/components/icons/Icon'

const banner = cva(
  'flex gap-3 items-start rounded-lg border p-3 text-sm',
  {
    variants: {
      variant: {
        info: 'bg-status-info-bg border-status-info-border text-status-info-text',
        success: 'bg-status-success-bg border-status-success-border text-status-success-text',
        warning: 'bg-status-warning-bg border-status-warning-border text-status-warning-text',
        error: 'bg-status-danger-bg border-status-danger-border text-status-danger-text',
      },
    },
    defaultVariants: { variant: 'info' },
  }
)

const iconMap = {
  info: 'info',
  success: 'check-circle',
  warning: 'alert-triangle',
  error: 'alert-circle',
} as const

export interface BannerProps extends VariantProps<typeof banner> {
  title?: string
  body?: ReactNode
  children?: ReactNode
  className?: string
}

export function Banner({ variant = 'info', title, body, children, className }: BannerProps) {
  const role = variant === 'error' || variant === 'warning' ? 'alert' : 'status'
  return (
    <div role={role} className={banner({ variant, className })}>
      <Icon
        name={iconMap[variant ?? 'info']}
        size={20}
        aria-hidden="true"
        className="flex-shrink-0 mt-0.5"
      />
      <div className="flex-1 min-w-0">
        {title && <p className="font-semibold leading-snug mb-0.5">{title}</p>}
        {body && <p className="leading-snug">{body}</p>}
        {children}
      </div>
    </div>
  )
}
